import { Form, FormikProvider, useFormik } from 'formik';
import { Steps } from 'antd';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import {
  ProductType,
  StoreProductPayload
} from '../../../models/Product/contracts';
import { Col, Row } from 'react-bootstrap';
import ProductInfo from './Steps/ProductInfo';
import ProductAttributes from './Steps/ProductAttributes';
import ProductFeatures from './Steps/ProductFeatures';
import * as Yup from 'yup';
import { Product } from '../../../models/Product/Product';
import { useNavigate } from 'react-router-dom';
import {
  CgArrowLongLeft as PreviousIcon,
  CgArrowLongRight as NextIcon
} from 'react-icons/cg';
import { collect, Collection } from 'collect.js';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import ProductSummary from './Steps/ProductSummary';
import { MediaResponse } from '../../../models/Media/contracts';
import ProductImages from './Steps/ProductImages';

export interface StoreProductValues {
  type: ProductType;
  category: number | undefined;
  storage_room_stand_id: number | undefined;
  name: string;
  sku: string;
  price: number;
  penalty_price: number;
  short_description: string;
  points: number;
  attributes: number[];
  features: number[];
  addons: number[];
  attribute_values: { [key: number]: number[] };
  feature_values: { [key: number]: number[] };
  cover_image_id: number | null;
  gallery_media: MediaResponse[];
  gallery_media_ids: number[];
  images: MediaResponse[]; // Added field to match validation schema
}

interface CreateProductFormProps {
  productType: ProductType;
  product?: Product;
}

function collectionToObject<Type = any>(collection: Collection<Type>): any {
  const result: { [key: string | number]: Type } = {};
  collection.each((item, key) => {
    if (!key) {
      return;
    }
    result[key] = item;
  });
  return result;
}
function convertProductToFormValues(
  product: Product,
  productType?: ProductType
): StoreProductValues {
  // Convert a Product model to the values expected by Formik form
  return {
    type: productType || product.type,
    category: product.category?.id ?? undefined,
    storage_room_stand_id: product.storageStand?.id ?? undefined,
    name: product.name ?? '',
    sku: product.sku ?? '',
    price: product.price ?? 0,
    penalty_price: product.penaltyPrice ?? 0,
    short_description: product.shortDescription ?? '',
    points: product.points ?? 0,
    cover_image_id: product.coverImage?.id ?? null,
    gallery_media: product.gallery ?? [],
    gallery_media_ids: product.gallery?.map((img) => img.id) ?? [],
    images: product.gallery ?? [],
    attributes:
      collect(product?.attributes).pluck('attribute_id').unique().toArray() ??
      [],
    features:
      collect(product?.features).pluck('feature_id').unique().toArray() ?? [],
    addons: [],
    attribute_values: collectionToObject(
      collect(product?.attributes)
        .groupBy('attribute_id')
        .mapWithKeys((value: any, attributeID: any) => {
          return [attributeID, value.pluck('id').toArray()];
        })
    ),
    feature_values: collectionToObject(
      collect(product?.features)
        .groupBy('feature_id')
        .mapWithKeys((value: any, attributeID: any) => {
          return [attributeID, value.pluck('id').toArray()];
        })
    )
  };
}

function CreateProductForm({
  productType,
  product
}: PropsWithChildren<CreateProductFormProps>) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const initialValues = useMemo(() => {
    const values: StoreProductValues = {
      type: productType,
      category: product?.category?.id ?? undefined,
      storage_room_stand_id: product?.storageStand?.id ?? undefined,
      name: product?.name ?? '',
      sku: product?.sku ?? '',
      price: product?.price ?? 0,
      penalty_price: product?.penaltyPrice ?? 0,
      short_description: product?.shortDescription ?? '',
      points: product?.points ?? 0,
      cover_image_id: product?.coverImage?.id ?? null,
      gallery_media: product?.gallery ?? [],
      gallery_media_ids: product?.gallery?.map((img) => img.id) ?? [],
      images: product?.gallery ?? [], // Add this field to match validation schema
      attributes:
        collect(product?.attributes).pluck('attribute_id').unique().toArray() ??
        [],
      features:
        collect(product?.features).pluck('feature_id').unique().toArray() ?? [],
      addons: [],
      attribute_values: collectionToObject(
        collect(product?.attributes)
          .groupBy('attribute_id')
          .mapWithKeys((value: any, attributeID: any) => {
            return [attributeID, value.pluck('id').toArray()];
          })
      ),
      feature_values: collectionToObject(
        collect(product?.features)
          .groupBy('feature_id')
          .mapWithKeys((value: any, attributeID: any) => {
            return [attributeID, value.pluck('id').toArray()];
          })
      )
    };
    return values;
  }, [product, productType]);

  const steps = useMemo(
    () => [
      {
        title: 'Detalji',
        content: <ProductInfo product={product} />,
        validationSchema: Yup.object({
          category: Yup.number()
            .integer()
            .required('Morate izabrati kategoriju.'),
          storage_room_stand_id: Yup.number()
            .integer()
            .required('Morate izabrati policu.'),
          name: Yup.string()
            .max(255, 'Ime može imati najviše 255 karaktera.')
            .required('Ime proizvoda je obavezno.'),
          sku: Yup.string()
            .max(255, 'SKU može imati najviše 255 karaktera.')
            .required('SKU je obavezan.'),
          price: Yup.number()
            .min(0, 'Cena mora biti veća ili jednaka nuli.')
            .required('Cena je obavezna.'),
          penalty_price: Yup.number()
            .min(0, 'Penal cena mora biti veća ili jednaka nuli.')
            .required('Penal cena je obavezna.'),
          short_description: Yup.string().max(
            500,
            'Kratak opis može imati najviše 500 karaktera.'
          ),
          points: Yup.number()
            .min(0, 'Broj bodova mora biti veći ili jednak nuli.')
            .required('Bodovi su obavezni.')
        })
      },
      {
        title: 'Slike',
        content: <ProductImages />,
        validationSchema: Yup.object({
          gallery_media_ids: Yup.array()
            .of(Yup.number())
            .min(1, 'Morate izabrati bar jednu sliku za galeriju.'),
          cover_image_id: Yup.number()
            .nullable()
            .required('Morate izabrati naslovnu sliku.')
        })
      },
      {
        title: 'Atributi',
        content: <ProductAttributes product={product} />,
        validationSchema: Yup.object({
          attributes: Yup.array()
            .of(Yup.number().integer())
            .min(1, 'Potrebno je izabrati bar jedan atribut.')
            .required('Atributi su obavezni.'),
          attribute_values: Yup.lazy((value) => {
            const shape: {
              [key: string]: Yup.ArraySchema<Yup.NumberSchema>;
            } = {};
            for (const key in value) {
              shape[key] = Yup.array()
                .of(Yup.number().integer())
                .min(1, ' Atribut mora imati bar jednu vrednost.')
                .required('Vrednosti atributa su obavezne.');
            }
            return Yup.object().shape(shape);
          })
        })
      },
      {
        title: 'Karakteristike',
        content: <ProductFeatures />,
        validationSchema: Yup.object({
          features: Yup.array()
            .of(Yup.number().integer())
            .min(1, 'Potrebno je izabrati bar jednu karakteristiku.')
            .required('Karakteristike su obavezne.'),
          feature_values: Yup.lazy((value) => {
            const shape: {
              [key: string]: Yup.ArraySchema<Yup.NumberSchema>;
            } = {};
            for (const key in value) {
              shape[key] = Yup.array()
                .of(Yup.number().integer())
                .min(
                  1,
                  'Vrednost karakteristike mora imati bar jednu vrednost.'
                )
                .required('Vrednosti karakteristike su obavezne.');
            }
            return Yup.object().shape(shape);
          })
        })
      },
      {
        title: 'Potvrda',
        content: (
          <>
            <ProductSummary />
          </>
        ),
        validationSchema: Yup.object({})
      }
    ],
    [product]
  );

  useEffect(() => {
    setStep(0);
  }, [productType]);

  const goForward = () => {
    setStep(Math.min(step + 1, steps.length - 1));
  };

  const goBackwards = () => {
    setStep(Math.max(step - 1, 0));
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: steps[step].validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      // --- This is for moving between steps ---
      if (step < steps.length - 1) {
        try {
          await steps[step].validationSchema.validate(values, {
            abortEarly: false
          });
          goForward();
        } catch (validationError: any) {
          const errors: Record<string, any> = {};
          const touched: Record<string, boolean> = {};
          if (validationError.inner) {
            validationError.inner.forEach((err: any) => {
              if (err.path) {
                errors[err.path] = err.message;
                touched[err.path] = true;
              }
            });
          }
          formik.setTouched(touched, true);
          formik.setErrors(errors);
        }
        return;
      }

      // --- FIX: The faulty validation check has been removed from here. ---
      // The logic now correctly proceeds to the final submission.

      // On the final step, prepare and send the data.
      const payload: StoreProductPayload = {
        type: values.type,
        category: values.category,
        storage_room_stand_id: values.storage_room_stand_id,
        name: values.name,
        sku: values.sku,
        price: values.price,
        penalty_price: values.penalty_price,
        short_description: values.short_description,
        points: values.points,

        addons: values.addons,
        attribute_values: [],
        feature_values: [],
        cover_image_id: values.cover_image_id,
        gallery_media_ids: values.gallery_media_ids
      };
      for (const key in values.attribute_values) {
        payload.attribute_values.push(...values.attribute_values[key]);
      }
      for (const key in values.feature_values) {
        payload.feature_values.push(...values.feature_values[key]);
      }

      setSubmitting(true);

      try {
        if (typeof product === 'undefined') {
          // --- CREATE LOGIC ---
          await Product.store(payload);
          toast.success('Proizvod je uspešno kreiran.');
          // --- FIX: Invalidate the products query to refresh the list ---
          await queryClient.invalidateQueries({ queryKey: ['admin-products'] });
          resetForm();
          setStep(0);
        } else if (product?.id) {
          // --- UPDATE LOGIC ---
          const updatedProduct = await product.update(payload);
          toast.success('Proizvod je uspešno izmenjen.');

          // --- FIX: Invalidate queries to refresh the product list and the single product view ---
          await queryClient.invalidateQueries({ queryKey: ['admin-products'] });
          await queryClient.invalidateQueries({
            queryKey: ['product', updatedProduct.id]
          });

          resetForm({
            values: convertProductToFormValues(updatedProduct, productType)
          });

          navigate('/admin/products/list');
        } else {
          throw new Error('Product object is missing or invalid.');
        }
      } catch (error: any) {
        console.error('Submission Error:', error);
        toast.error(error?.response?.data?.message || 'Došlo je do greške.');
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" className="cs-form-wrapper">
        <Steps current={step}>
          {steps.map((item) => (
            <Steps.Step key={item.title} title={item.title} />
          ))}
        </Steps>
        <Row>
          <Col>{steps[step].content}</Col>
        </Row>
        <Row>
          <Col className="d-flex justify-content-between align-items-center">
            {step > 0 && (
              <button
                className="cs-button-secondary"
                type="button"
                onClick={() => goBackwards()}
              >
                <PreviousIcon />
                <span>Nazad</span>
              </button>
            )}
            {step < steps.length - 1 && (
              <button className="cs-button-primary" type="submit">
                <span>Napred</span>
                <NextIcon />
              </button>
            )}
            {step === steps.length - 1 && (
              <button
                className="cs-button-primary"
                type="submit"
                disabled={formik.isSubmitting}
              >
                <span>Završi</span>
              </button>
            )}
          </Col>
        </Row>
      </Form>
    </FormikProvider>
  );
}

export default CreateProductForm;
